import axios from 'axios';
import { API_URL } from '../../config/api';
import { utcTime } from '../users/model';

export interface UserStatus {
  id: number; user_id: number; user_username: string | null; user_name: string | null;
  user_avatar: string | null; status: string; status_message: string | null;
  checked_in_place: string | null; checked_in_at: string | null;
  destination_name: string | null; estimated_arrival_time: string | null;
  bubble_active: boolean; bubble_reason: string | null; bubble_expires_at: string | null;
  updated_at: string | null;
}
export interface StatusStats {
  total_statuses: number; bubble_active: number; checked_in: number; on_my_way: number;
  by_status: { status: string; count: number }[];
}
export interface Filters { status: string; privacy: '' | 'active' | 'inactive'; place: boolean }
export const defaults: Filters = { status: '', privacy: '', place: false };
export const labels: Record<string, string> = { available: 'Müsait', on_my_way: 'Yolda', checked_in: 'Yer bildirimi', busy: 'Meşgul', offline: 'Çevrimdışı' };
export const statusLabel = (status: string) => Object.prototype.hasOwnProperty.call(labels, status) ? labels[status] : status || 'Belirtilmemiş';
export const toneOf = (status: string) => Object.prototype.hasOwnProperty.call(labels, status) ? status : 'other';
export const nameOf = (item: UserStatus) => item.user_name || (item.user_username !== 'Unknown' && item.user_username) || `Kullanıcı #${item.user_id}`;
export const usernameOf = (item: UserStatus) => item.user_username && item.user_username !== 'Unknown' ? `@${item.user_username}` : 'Kullanıcı adı yok';
export function reasonLabel(reason: string | null) {
  const reasons: Record<string, string> = { surprise: 'Sürpriz', meeting: 'Toplantı', resting: 'Dinleniyorum', work: 'Toplantı', personal: 'Kişisel', other: 'Diğer' };
  return reason ? (Object.prototype.hasOwnProperty.call(reasons, reason.toLowerCase()) ? reasons[reason.toLowerCase()] : reason) : 'Belirtilmemiş';
}
export function relativeTime(value: string | null, now = Date.now()) {
  const time = value ? utcTime(value) : NaN;
  if (!Number.isFinite(time)) return 'Tarih yok';
  const minutes = Math.floor((now - time) / 60000);
  if (minutes < -1) return 'İleri tarihli kayıt';
  if (minutes < 1) return 'Az önce';
  if (minutes < 60) return `${minutes} dk önce`;
  if (minutes < 1440) return `${Math.floor(minutes / 60)} sa önce`;
  return `${Math.floor(minutes / 1440)} gün önce`;
}
export function matches(item: UserStatus, search: string, filters: Filters) {
  const text = [item.id, item.user_id, item.user_name, item.user_username, item.status_message, item.checked_in_place, item.destination_name, statusLabel(item.status)].join(' ').toLocaleLowerCase('tr-TR');
  return text.includes(search.trim().toLocaleLowerCase('tr-TR')) && (!filters.status || item.status === filters.status) &&
    (!filters.privacy || item.bubble_active === (filters.privacy === 'active')) && (!filters.place || !!item.checked_in_place);
}
export type Sort = 'updated' | 'name';
export function sortItems(items: UserStatus[], sort: Sort) {
  return [...items].sort((a, b) => sort === 'name' ? nameOf(a).localeCompare(nameOf(b), 'tr-TR') || a.id - b.id :
    (b.updated_at && Number.isFinite(utcTime(b.updated_at)) ? utcTime(b.updated_at) : 0) -
    (a.updated_at && Number.isFinite(utcTime(a.updated_at)) ? utcTime(a.updated_at) : 0) || b.id - a.id);
}
export async function fetchStatuses() {
  const items: UserStatus[] = [], seen = new Set<number>();
  let skip = 0, total = 0;
  while (skip < 100000) {
    const response = await axios.get<{total: number; statuses: UserStatus[]}>(`${API_URL}/admin/user-statuses`, { params: { skip, limit: 1000 } });
    const data = response.data;
    if (!Array.isArray(data?.statuses) || !Number.isInteger(data.total) || data.total < 0) throw new Error('Invalid status list');
    total = data.total;
    if (!data.statuses.length && skip < total) throw new Error('Incomplete status list');
    for (const item of data.statuses) if (!seen.has(item.id)) { items.push(item); seen.add(item.id); }
    skip += data.statuses.length;
    if (skip >= total) break;
    if (!data.statuses.length) break;
  }
  return { items, total, partial: items.length < total };
}
